import { z } from 'zod';

import { Activity, BookingOption, Location } from './Activity';
import Ledger from './Ledger';

const ORDER_STATUSES = {
  Cancelled: 'Cancelled',
  Draft: 'Draft',
  Fulfilled: 'Confirmed',
  Pending: 'Pending',
  Requested: 'Requested',
  Abandoned: 'Abandoned',
  PaymentFailure: 'Payment Failure',
} as const;

type OrderStatus = keyof typeof ORDER_STATUSES;
// What statuses should there be?
type OrderPaymentStatus = string | Record<string, unknown>;
type OrderCommunicationStatus = Record<string, unknown>;

export enum OrderPaymentDetailsObject {
  charge = 'charge',
  paymentIntent = 'payment_intent',
  refund = 'refund',
  transfer = 'transfer',
  transferReversal = 'transfer_reversal',
}

export type OrderPaymentDetails = {
  amount: number;
  created: number;
  id: string;
  object: OrderPaymentDetailsObject;
  status: string | null;
  parent?: string;
};

export type Reason =
  | 'duplicate'
  | 'requested_by_customer'
  | 'fraudulent'
  | 'other';

export type OrderRefundPayload = {
  refundAmount: number;
  reverseAmount?: number;
  reason?: Reason;
  reasonNote?: string;
  relatedLedgerEntryId: string;
};

export type BookingReference = {
  identifier: string;
  url?: string;
};

export interface Order {
  activity: Activity;
  activityId?: string;
  additionalCharges?: AdditionalCharge[];
  arrival?: OrderArrival;
  bachConfirmation?: string;
  baseSubtotalPrice?: string;
  bookingOptions?: OrderBookingOption[];
  booking?: any;
  cardId?: string;
  created?: Date;
  customer?: Customer;
  description?: string;
  discount?: OrderDiscount;
  endDateTime?: Date | string;
  eventId?: string;
  id?: string;
  lineItems?: LineItem[];
  name?: string;
  note?: string;
  partyId?: string;
  partyTz?: string;
  startDateTime?: Date | string;
  status: OrderStatus;
  stripeChargeIds?: string[];
  subtotalPrice?: number;
  taxRate?: string;
  totalNonCommission?: string;
  totalPrice: string;
  totalTax?: string;
  updated?: Date;
  vendorConfirmation?: string;
  processingFee?: string;
  paymentStatus?: OrderPaymentStatus;
  communicationStatus?: OrderCommunicationStatus[];
  ledger: Ledger;
  paymentErrorMessage?: string;
}

type Customer = {
  location?: Location;
  memberEmail: string;
  memberId: string;
  memberName: string;
  memberPhone?: string;
  memberRole: string;
  partyId: string;
};

export interface OrderDiscount {
  amount: string;
  description: string;
  title: string;
  value: string;
  value_type: string;
  original?: Omit<OrderDiscount, 'original'>;
}

interface OrderArrival {
  display_text: string;
  notes: string;
  notes_safe_html: string;
  time: string;
}

interface LineItem {
  variant_id: string;
  price: number;
  quantity: number;
  requires_shipping: boolean;
  title: string;
}
interface AdditionalCharge {
  name: string;
  percent: number;
  amount: number;
}

export interface OrderBookingOption {
  id: string;
  lineItemId: string;
  attendees: OrderAttendees[];
  location?: Location;
  timeslot?: string;
  tax?: string;
  altTax?: string;
}

export interface OrderAttendees {
  id: string;
  name: string;
}

export type FullOrderBookingOption = BookingOption & OrderBookingOption;

const updatedBySchema = z.object({
  email: z.string().optional(),
  name: z.string().optional(),
  sub: z.string().optional(),
});
const orderHistoryRecordSchema = z.object({
  updatedAt: z.string().datetime().describe('Datetime when change was made'),
  updatedBy: updatedBySchema
    .optional()
    .describe('Information about who made the change'),
  status: z.string().describe('Order status after update'),
});
export type OrderHistoryRecord = z.infer<typeof orderHistoryRecordSchema>;

export type OrderRequestRefundPayload = {
  refundAmount: number;
  reverseAmount?: number;
  reason?: Reason;
  reasonNote?: string;
};
